import { Request as request } from "@/utils/request";

// ============================================================================
//  工具函数
// ============================================================================

/**
 * 生成随机 key (6-18 位)
 * @returns {string} 随机字符串
 */
export const generateRandomKey = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const length = Math.floor(Math.random() * 13) + 6; // 6-18 位
  let key = "";
  for (let i = 0; i < length; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
};

// ============================================================================
//  用户资料统一更新接口 (新版)
// ============================================================================

/**
 * 更新用户资料
 * @param {Object} params - 更新字段（至少传一个）
 * @param {string} [params.nickname] - 昵称（1-64字符）
 * @param {string} [params.photo_url] - 头像URL（最长500）
 * @param {string} [params.mobile] - 手机号（最长32）
 * @param {string} [params.payment_type] - 付款类型（最长32）
 * @param {string} [params.payment_info] - 付款信息（最长500）
 * @param {number} [params.task01] - 自定义任务1
 * @param {number} [params.task02] - 自定义任务2
 * @param {number} [params.task03] - 自定义任务3
 * @param {number} [params.task04] - 自定义任务4
 * @param {number} [params.task05] - 自定义任务5
 * @returns {Promise<{ code: number, message: string, data: { user: Object } }>}
 */
export const updateProfile = (params) => {
  return request("/api/user/profile", {
    method: "PUT",
    params,
  });
};

// ============================================================================
//  兼容旧接口 (部分仍在使用)
// ============================================================================

/** 更新余额 - index.vue 使用 */
let updateTaskV1 = (params) => {
  return request("/api/miner/update-task", {
    method: "POST",
    params,
  });
};

/** OKX 钱包地址保存 - OKXStore.js 使用 */
let updateWalletOKX = (params) => {
  return request("/api/common/save-okx-address", {
    method: "POST",
    params,
  });
};

/**
 * 获取我的下级列表
 * @param {{
 *   page?: number,
 *   limit?: number
 * }} params
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{ uid, nickname, email, created_at, active_at }>,
 *     pagination: { page, limit, total, total_pages }
 *   }
 * }>}
 */
let getFriends = (params = {}) => {
  const { page = 1, limit = 20 } = params;
  return request(`/api/auth/referrals?page=${page}&limit=${limit}`, {
    method: "GET",
  });
};

/**
 * 获取提现记录
 * @param {{
 *   page?: number,
 *   pageSize?: number,
 *   audit_status?: number,
 *   withdrawals_type?: number
 * }} params
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: string,
 *       address: string,
 *       audit_status: number,
 *       coin_name: string,
 *       created_at: number,
 *       send_amount: number
 *     }>,
 *     pagination: {
 *       page: number,
 *       pageSize: number,
 *       total: number,
 *       totalPages: number
 *     }
 *   }
 * }>}
 */
export const getWithdrawals = (params = {}) => {
  const { page = 1, pageSize = 20, audit_status, withdrawals_type } = params;
  const queryParams = new URLSearchParams();
  if (page) queryParams.append("page", page);
  if (pageSize) queryParams.append("pageSize", pageSize);
  if (audit_status !== undefined && audit_status !== null)
    queryParams.append("audit_status", audit_status);
  if (withdrawals_type !== undefined && withdrawals_type !== null)
    queryParams.append("withdrawals_type", withdrawals_type);

  const queryString = queryParams.toString();
  return request(
    `/api/wallet/withdrawals${queryString ? `?${queryString}` : ""}`,
    {
      method: "GET",
    },
  );
};

/**
 * 余额提现
 * @param {{
 *   amount: string | number,
 *   address: string,
 *   token_name: string,
 *   over_task?: string | number,
 *   share?: string | number,
 *   send_message_num?: string | number
 * }} params
 * @param {string} [cfTurnstileResponse]
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data?: any
 * }>}
 */
export const withdrawBalance = (params, cfTurnstileResponse) => {
  return request("/api/wallet/balance/withdraw", {
    method: "POST",
    params,
    headers: {
      "x-idempotency-key": generateRandomKey(),
      ...(cfTurnstileResponse ? { "cf-turnstile-response": cfTurnstileResponse } : {}),
    },
  });
};

/**
 * 佣金提现
 * @param {{
 *   amount: string | number,
 *   address: string,
 *   wallet_type: string
 * }} params
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data?: any
 * }>}
 */
export const withdrawCommission = (params) => {
  return request("/api/wallet/commission/withdraw", {
    method: "POST",
    params,
  });
};

/**
 * 加速提现
 * @param {{
 *   share?: string | number,
 *   over_task?: string | number,
 *   send_message_num?: string | number
 * }} params
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data?: any
 * }>}
 */
export const accelerateWithdraw = (params) => {
  return request("/api/withdraw-accelerate", {
    method: "POST",
    params,
  });
};

/** OKX 订单更新 - OKXStore.js 使用 */
let updateOrdersOKX = (params) => {
  return request("/api/common_v1/update-order", {
    method: "POST",
    params,
  });
};

// /api/common/valid-user
/**
 * 验证用户
 * @param {{
 *
 * }} params
 * @returns
 */
let validUser = () => {
  return request("/api/common/valid-user", {
    method: "POST",
  });
};
/**
 * 兑换
 * @param {
 * amount:number
 * } params
 * @returns
 */
let exchange = (params) => {
  return request("/api/wallet/exchange-asset", {
    method: "POST",
    params,
  });
};

/**
 * 领取邀请奖励
 * @param {{
 *   reward_index: number
 * }} params - 奖励阶梯索引（从0开始）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     reward_index: number,
 *     required_invites: number,
 *     reward_points: number,
 *     new_points: number
 *   }
 * }>}
 */
export const claimInviteReward = (params) => {
  return request("/api/user/invite-reward/claim", {
    method: "POST",
    params,
  });
};

export {
  getFriends,
  updateTaskV1,
  updateWalletOKX,
  updateOrdersOKX,
  validUser,
  exchange,
};
